import React from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import { HTTPError } from 'ky';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { ObservationType } from '@interfaces-clean/observations.interface';
import styles from '@pages-clean/admin/study/admin-data-views/data-views/details/style.module.scss';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';
import SiteReviewLogsApi from '@apis/bff-api/central-monitoring/site-review-log';
import ReviewLogsApi from '@apis/bff-api/central-monitoring/study-review-log';

export interface ReviewLogDeleteDialogProps {
    recordId: string;
    onClose: () => void;
    studyRef: string;
    onDeleteHandler: (success: boolean, conflict?: boolean) => void;
    reviewLogId: number;
    observationId: string | null | undefined;
    type: ObservationType;
    reviewLogDatasetDate: string | null | undefined;
}

export type FormInputs = {
    deleteReason: string | null;
};

const DeleteReviewLogDialog = ({
    recordId,
    onClose,
    studyRef,
    onDeleteHandler,
    reviewLogId,
    observationId,
    type,
    reviewLogDatasetDate,
}: ReviewLogDeleteDialogProps) => {
    const { t } = useTranslation('cm');
    const { trigger: deleteReviewLog } = ReviewLogsApi.useDelete(studyRef);
    const { trigger: deleteSiteReviewLog } = SiteReviewLogsApi.useDelete(studyRef);
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const { data: observationDTO } = ObservationsApi.useGet(studyRef, observationId || '');

    const onDelete = async (data: FormInputs) => {
        const deleteHandler =
            type === ObservationType.Site
                ? (deleteSiteReviewLog as (args: {
                      deletedReason: string | null;
                      reviewLogId: number;
                      observationId?: string | null;
                      datasetDate?: string | null;
                  }) => Promise<boolean>)
                : (deleteReviewLog as (args: {
                      deletedReason: string | null;
                      reviewLogId: number;
                      observationId?: string | null;
                      datasetDate?: string | null;
                  }) => Promise<boolean>);

        try {
            const response: boolean = await deleteHandler({
                deletedReason: data.deleteReason,
                reviewLogId,
                observationId,
                datasetDate: reviewLogDatasetDate,
            });
            onDeleteHandler(response, false);
        } catch (err) {
            const status = (err as HTTPError)?.response?.status;
            onDeleteHandler(false, status === 409);
        }
    };

    const maxChars = 350;

    // We need to append the additional height to the dialog depending on the observation length to avoid scroll bar.
    // We need to show a maximum of 5 lines for observation text which is approx 350 chars. Calculating the no of lines
    // and then appending 15 px height per line. 270 is the minimum required length. 65 px is if the number of lines is more than 5.
    const minDialogHeight = 280;
    const allowedAdditionalMaxHeight = 65;
    const approxCharsPerLine = 70;

    const calculatedHeight =
        (observationDTO?.observation?.length as unknown as number) > maxChars
            ? allowedAdditionalMaxHeight
            : ((observationDTO?.observation?.length as unknown as number) / approxCharsPerLine) * 15;

    const warningText = (
        <span className={cn('k-w-full k-d-flex k-flex-column k-gap-2', styles.fontSize)}>
            <div>
                <span className="k-font-medium k-color-primary">{t('cm:reviewLogGrid.deleteReviewLogWarning1')} </span>
                <span className="k-font-medium k-color-secondary">
                    {stringDateWithoutTimeFromUtc(reviewLogDatasetDate, 'DD-MMM-YYYY')}
                </span>
            </div>
            <div className={style.deleteWarningOverflow}>
                <span className="k-font-medium">{t('cm:reviewLogGrid.deleteReviewLogWarning2')}</span>{' '}
                <span className={style.textHighLightNoBold}>{observationDTO && observationDTO.observation}</span>
            </div>
        </span>
    );

    return (
        observationDTO && (
            <DraggableDialog
                dataTestId="review-log-delete-dialog"
                onSubmit={() => handleSubmit(onDelete)()}
                onClose={onClose}
                title={`${t('cm:reviewLogGrid.deleteReviewLog')}: ${recordId}`}
                initialHeight={minDialogHeight + calculatedHeight}
                initialWidth={520}
                primaryBtnLabel={t('common:delete')}
                primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
                secondaryBtnDisabled={isSubmitting}
            >
                <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                    <InfoField type="Warning" message={warningText} />
                    <TextAreaController
                        className={style.textAreaOverrides}
                        maxLength={3000}
                        control={control}
                        name="deleteReason"
                        required
                        data-testid="delete-review-log"
                        label={`${t('cm:deleteReasonLabel')} *`}
                        readOnly={isSubmitting}
                    />
                </div>
            </DraggableDialog>
        )
    );
};

export default DeleteReviewLogDialog;
